import { useEffect, useMemo, useRef } from 'react';
import Lottie from 'lottie-react';

import hatPolonaiseDown from '@/playlist/games/letterParty/assets/lottie/hat-polonaise-down.json';
import hatPolonaiseRight from '@/playlist/games/letterParty/assets/lottie/hat-polonaise-right.json';
import hatPolonaiseUp from '@/playlist/games/letterParty/assets/lottie/hat-polonaise-up.json';
import leadDown from '@/playlist/games/letterParty/assets/lottie/lead-down.json';
import leadRight from '@/playlist/games/letterParty/assets/lottie/lead-right.json';
import leadUp from '@/playlist/games/letterParty/assets/lottie/lead-up.json';
import polonaiseDown from '@/playlist/games/letterParty/assets/lottie/polonaise-down.json';
import polonaiseRight from '@/playlist/games/letterParty/assets/lottie/polonaise-right.json';
import polonaiseUp from '@/playlist/games/letterParty/assets/lottie/polonaise-up.json';

import type { CSSProperties } from 'react';
import type { LottieRefCurrentProps } from 'lottie-react';

interface GridPosition {
	x: number;
	y: number;
}

interface SnakeCharacterProps {
	isLeader?: boolean;
	size?: number;
	position?: GridPosition;
	showHat?: boolean;
	movingDirection?: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';
	segmentIndex?: number;
	animationSyncToken?: number;
	isMoving?: boolean;
}

type AnimationDirection = 'UP' | 'DOWN' | 'RIGHT';

const SnakeCharacter = ({
	isLeader = false,
	size = 45,
	position,
	showHat = false,
	movingDirection = 'RIGHT',
	segmentIndex = 0,
	animationSyncToken = 0,
	isMoving = true,
}: SnakeCharacterProps) => {
	const lottieRef = useRef<LottieRefCurrentProps | null>(null);
	const bodySize = Math.max(20, size * 1.6);

	// Offset to bring segments closer together
	// Each segment gets progressively more offset to close gaps throughout the snake
	const offsetPerSegment = size * 0.03;
	const totalOffset = segmentIndex * offsetPerSegment;
	let leftOffset = 0;
	let topOffset = 0;

	if (!isLeader) {
		// Offset towards the direction of movement to close gap with all segments ahead
		switch (movingDirection) {
			case 'UP':
				topOffset = -totalOffset;
				break;
			case 'DOWN':
				topOffset = totalOffset;
				break;
			case 'LEFT':
				leftOffset = -totalOffset;
				break;
			case 'RIGHT':
				leftOffset = totalOffset;
				break;
		}
	}

	const wrapperStyle: CSSProperties = {
		position: 'absolute',
		width: size,
		height: size,
		left: (position?.x ?? 0) * size + leftOffset,
		top: (position?.y ?? 0) * size + topOffset,
		zIndex: 100 + (position?.y ?? 0), // Higher y = higher z-index (bottom segments appear above)
	};

	const normalizedDirection: AnimationDirection =
		movingDirection === 'LEFT' ? 'RIGHT' : (movingDirection as AnimationDirection);
	const isFacingLeft = movingDirection === 'LEFT';

	// Get the current animation data based on direction
	const currentAnimationData = useMemo(() => {
		const animationMap: Record<AnimationDirection, unknown> = {
			UP: isLeader ? leadUp : showHat ? hatPolonaiseUp : polonaiseUp,
			DOWN: isLeader ? leadDown : showHat ? hatPolonaiseDown : polonaiseDown,
			RIGHT: isLeader ? leadRight : showHat ? hatPolonaiseRight : polonaiseRight,
		};

		return animationMap[normalizedDirection];
	}, [isLeader, showHat, normalizedDirection]);

	// Control animation play/pause based on movement state
	useEffect(() => {
		if (lottieRef.current) {
			if (isMoving) {
				lottieRef.current.goToAndPlay(0, true);
			} else {
				lottieRef.current.goToAndStop(0, true);
			}
		}
	}, [currentAnimationData, animationSyncToken, isMoving]);

	return (
		<div
			className="letter-party-game__snake-segment letter-party-game__q-character"
			style={wrapperStyle}
		>
			<div className="letter-party-game__q-character-inner">
				<div
					className="letter-party-game__q-character-letter-wrapper"
					data-variant="snake"
					style={{
						width: bodySize,
						height: bodySize,
						position: 'relative',
						transform: isFacingLeft ? 'scaleX(-1)' : undefined,
						transformOrigin: 'center',
					}}
				>
					<Lottie
						lottieRef={lottieRef}
						animationData={currentAnimationData}
						loop
						autoplay={false}
						className="letter-party-game__q-character-letter-art"
					/>
				</div>
			</div>
		</div>
	);
};

export default SnakeCharacter;
